<script setup lang="ts">
import { ref, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import BTooltipBtn from '@/components/base/BTooltipBtn/BTooltipBtn.vue'
import FLoginButtons from '@/components/feature/FLoginButtons/FLoginButtons.vue'
import type { AuthProvider } from '@/composables/useAuth.ts'
import type { LoginMethodsView, LoginProvider } from './useLoginMethods'
import mdiEmail from '~icons/mdi/email'
import mdiPencil from '~icons/mdi/pencil'

defineProps<{
  email: string
  view: LoginMethodsView
}>()

const rememberMe = defineModel<boolean>('rememberMe', { required: true })

const emit = defineEmits<{
  editEmail: []
  notYou: []
  select: [provider: AuthProvider]
}>()

const { t } = useI18n()
const showOthers = ref(false)
const othersId = useId()

function primaryLabel(provider: LoginProvider): string {
  return provider.id === 'email'
    ? t('common.sign_in_with_otp')
    : t('common.sign_in_with_provider', { provider: t(provider.title) })
}
</script>

<template>
  <div>
    <div class="d-flex align-center flex-wrap ga-2 mb-2">
      <span>{{ email }}</span>
      <b-tooltip-btn
        :icon="mdiPencil"
        size="small"
        variant="text"
        tooltip="change"
        @click="emit('editEmail')"
      />
      <v-btn
        variant="text"
        size="small"
        @click="emit('notYou')"
      >
        {{ t('common.not_you') }}
      </v-btn>
    </div>

    <v-checkbox
      v-model="rememberMe"
      :label="t('common.remember_me')"
      color="primary"
      class="mb-4"
    />

    <div class="d-flex flex-wrap ga-4">
      <v-btn
        v-for="provider in view.primary"
        :key="provider.id"
        color="primary"
        @click="emit('select', provider.id)"
      >
        <!-- The one-time code gets a font icon so it follows the button's text colour; the OAuth logos are brand images. -->
        <template
          v-if="provider.id === 'email'"
          #prepend
        >
          <v-icon :icon="mdiEmail" />
        </template>
        <template
          v-else-if="provider.image != null"
          #prepend
        >
          <img
            :src="provider.image"
            alt=""
            height="20"
          />
        </template>
        {{ primaryLabel(provider) }}
      </v-btn>
    </div>

    <template v-if="view.others.length > 0">
      <v-btn
        variant="text"
        size="small"
        class="mt-6 px-0 text-decoration-underline"
        :aria-expanded="showOthers"
        :aria-controls="othersId"
        @click="showOthers = !showOthers"
      >
        {{ t('common.other_login_options') }}
      </v-btn>
      <section
        v-if="showOthers"
        :id="othersId"
      >
        <v-divider class="my-6" />
        <p class="mb-4">{{ t('common.other_login_options') }}</p>
        <f-login-buttons
          :providers="view.others"
          @select="emit('select', $event)"
        />
      </section>
    </template>
  </div>
</template>
